/**
 * URL-only cast payloads (QR opens a guest page). Secrets live in the query string;
 * no server round-trip — treat links as sensitive.
 */

const CAST_VERSION = 1 as const;

export type PartyCastLayout =
  | "imposter_role"
  | "imposter_word"
  | "guess_word"
  | "acting_word"
  | "who_word"
  | "quiz"
  | "topic"
  | "dare";

export type PartyCastPayloadV1 = {
  v: typeof CAST_VERSION;
  shell: string;
  layout: PartyCastLayout;
  rtl: boolean;
  /** Primary word */
  w?: string;
  /** Forbidden words (guess) */
  f?: string[];
  /** Crew hints for imposter_word layout (parallel to secret word reveal) */
  hints?: string[];
  q?: string;
  a?: string;
  /** Topic / category label */
  t?: string;
  /** Dare body (prefer over `w` for layout "dare") */
  d?: string;
  /** Free headings/body (e.g. imposter role copy from host locale) */
  h1?: string;
  h2?: string;
  h3?: string;
};

function utf8ToBase64Url(json: string): string {
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  const b64 = btoa(bin);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

function base64UrlToUtf8(b64url: string): string | null {
  try {
    const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4;
    const padded = pad ? b64 + "====".slice(0, 4 - pad) : b64;
    const bin = atob(padded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

export function encodePartyCastPayload(payload: PartyCastPayloadV1): string {
  return utf8ToBase64Url(JSON.stringify(payload));
}

export function decodePartyCastPayload(raw: string | null | undefined): PartyCastPayloadV1 | null {
  if (!raw || !raw.trim()) return null;
  try {
    const decoded = base64UrlToUtf8(decodeURIComponent(raw.trim()));
    if (!decoded) return null;
    const o = JSON.parse(decoded) as PartyCastPayloadV1;
    if (o?.v !== CAST_VERSION || typeof o.shell !== "string" || typeof o.layout !== "string") return null;
    return o;
  } catch {
    return null;
  }
}

export function buildPartyCastRevealUrl(shellId: string, payload: PartyCastPayloadV1): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const d = encodePartyCastPayload(payload);
  return `${origin}/party/cast/${encodeURIComponent(shellId)}?d=${encodeURIComponent(d)}`;
}
